import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";

import { useUIStore } from "@/stores/useUIStore";

import HomeView from "@/views/HomeView.vue";
import SearchView from "@/views/SearchView.vue";
import ProfileView from "@/views/ProfileView.vue";
import GeneralConditionsView from "@/views/GeneralConditions.vue";
import LegalNoticeView from "@/views/LegalNotice.vue";

import CheckoutPrebookSuccessView from "@/views/checkout/CheckoutPrebookSuccessView.vue";
import CheckoutThankYouView from "@/views/checkout/CheckoutThankYouView.vue";
import CheckoutPage from "../views/checkout/CheckoutPage.vue";
import StripeCallbackView from "@/views/checkout/StripeCallbackView.vue";
import CheckoutKoView from "@/views/checkout/CheckoutKoView.vue";
import ClassView from "@/views/ClassView.vue";
import ProfessionalsDeckSpView from "@/views/ProfessionalsDeckSpView.vue";

//Login/Register
import LoginView from "@/views/loginRegister/LoginView.vue";
import RegisterView from "@/views/loginRegister/RegisterView.vue";
import ResetPasswordView from "@/views/loginRegister/ResetPasswordView.vue";

//Account
import AccountSettingsView from "@/views/account/AccountSettingsView.vue";
import FavoriteSpecialistsView from "@/views/account/FavoriteSpecialistsView.vue";
import PastAppointmentsView from "@/views/account/PastAppointmentsView.vue";
import UpcomingAppointmentsView from "@/views/account/UpcomingAppointmentsView.vue";

//Blog
import ArticlesView from "@/views/blog/ArticlesView.vue";
import CategorieView from "@/views/blog/CategorieView.vue";
import BlogPostView from "@/views/blog/BlogPostView.vue";

//Layout
import BaseLayout from "@/layouts/baseLayout.vue";
import SearchLayout from "@/layouts/searchLayout.vue";
import CheckoutLayout from "@/layouts/checkoutLayout.vue";
import AccountLayout from "@/layouts/accountLayout.vue";
import LoginRegisterLayout from "@/layouts/loginRegisterLayout.vue";
import BlogLayout from "@/layouts/blogLayout.vue";
import CustomerActivateLayout from "@/layouts/customerActivateLayout.vue";
import { extractTenantId } from "@/helpers/tenantHelper";
import { setSeo } from "@/composables/useSeo";

// Pages that should never be indexed
const noindexRoutes = new Set([
  "Login", "Register", "ResetPassword",
  "AccountSettings", "FavoriteSpecialists", "PastAppointments", "UpcomingAppointments",
  "CheckoutPrebookSuccess", "BookingSuccess", "BookingKo", "StripeCallback",
]);

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    component: BaseLayout,
    children: [
      {
        path: "",
        name: "Home",
        component: HomeView,
      },
      {
        path: "for-professionals",
        name: "ForProfessionals",
        component: ProfessionalsDeckSpView,
        meta: { hideFooter: true },
      },
      {
        path: "for-professionals-functionality",
        redirect: { name: "ForProfessionals" },
      },
      {
        path: "general-conditions",
        name: "GeneralConditions",
        component: GeneralConditionsView,
      },
      {
        path: "aviso-legal",
        name: "LegalNotice",
        component: LegalNoticeView,
      },
      {
        path: "live-groups",
        name: "Groups",
        component: () => import("@/views/groups/GroupsView.vue"),
      },
      {
        path: "live-groups/:diseaseKey",
        name: "GroupDetail",
        component: () => import("@/views/groups/GroupsView.vue"),
      },
      {
        path: "g/:slug",
        name: "GroupPrivate",
        component: () => import("@/views/groups/GroupsView.vue"),
      },
      {
        path: "session/:sessionSlug",
        name: "SessionDetail",
        component: () => import("@/views/groups/SessionView.vue"),
      },
      {
        path: "confirm/:token",
        name: "SessionConfirm",
        component: () => import("@/views/groups/SessionConfirmView.vue"),
      },
    ],
  },
  {
    path: "/search",
    component: SearchLayout,
    children: [
      {
        path: "/search",
        name: "Search",
        component: SearchView,
      },
      {
        path: "/profile/:tenantId/:id",
        name: "Profile",
        component: ProfileView,
      },
      {
        path: "/profile/:id",
        name: "ProfilePlain",
        component: ProfileView,
        beforeEnter: (to) => {
          const normalize = (value: unknown): string | undefined =>
            Array.isArray(value)
              ? (value[0] as string)
              : (value as string | undefined);
          const tenantFromQuery = normalize(to.query.tenantId);
          const tenantIgniteFromQuery = normalize(to.query.tenantXId);
          const resolvedTenant =
            tenantFromQuery ||
            (tenantIgniteFromQuery
              ? extractTenantId(tenantIgniteFromQuery)
              : null);

          if (resolvedTenant) {
            return {
              name: "Profile",
              params: { tenantId: resolvedTenant, id: to.params.id },
            };
          }

          return true;
        },
      },
      {
        path: "/class/:tenantId/:therapistId/:date",
        name: "ClassDetail",
        component: ClassView,
      },
      // Public SEO route at root: medkus.com/{slug}. Renders the profile view directly
      // — no redirect — so the slug URL stays as the canonical, SEO-friendly URL.
      {
        path: "/:tenantSlug([a-z][a-z0-9-]+)",
        name: "ProfileBySlug",
        component: ProfileView,
        beforeEnter: async (to) => {
          try {
            const r = await fetch(
              `https://naavi.medkus.com/api/seo/resolve/${to.params.tenantSlug}`,
            );
            if (!r.ok) return { path: "/" };
            const { tenantId, userId } = await r.json();
            if (!tenantId || !userId) return { path: "/" };
            to.params.tenantId = tenantId;
            to.params.id = String(userId);
            return true;
          } catch {
            return { path: "/" };
          }
        },
      },
    ],
  },
  {
    path: "/checkout",
    component: CheckoutLayout,
    children: [
      {
        path: "",
        component: CheckoutPage,
        children: [
          {
            path: "",
            name: "CheckoutPrebookSuccess",
            component: CheckoutPrebookSuccessView,
          },
          {
            path: "booking-success",
            name: "BookingSuccess",
            component: CheckoutThankYouView,
          },
          {
            path: "ko",
            name: "BookingKo",
            component: CheckoutKoView,
          },
        ],
      },
    ],
  },
  {
    path: "/stripe-callback",
    component: CheckoutLayout,
    children: [
      {
        path: "",
        name: "StripeCallback",
        component: StripeCallbackView,
      },
    ],
  },
  {
    path: "/loginRegister",
    component: LoginRegisterLayout,
    children: [
      {
        path: "/login",
        name: "Login",
        component: LoginView,
      },
      {
        path: "/register",
        name: "Register",
        component: RegisterView,
      },
      {
        path: "/customer/reset-password/:token/:isMedkus",
        name: "ResetPassword",
        component: ResetPasswordView,
      },
    ],
  },
  {
    path: "/account",
    component: AccountLayout,
    children: [
      {
        path: "/account/settings",
        name: "AccountSettings",
        component: AccountSettingsView,
      },
      {
        path: "/account/favorite-specialists",
        name: "FavoriteSpecialists",
        component: FavoriteSpecialistsView,
      },
      {
        path: "/account/past-appointments",
        name: "PastAppointments",
        component: PastAppointmentsView,
      },
      {
        path: "/account/upcoming-appointments",
        name: "UpcomingAppointments",
        component: UpcomingAppointmentsView,
      },
    ],
  },
  {
    path: "/blogs",
    component: BlogLayout,
    children: [
      {
        path: "",
        name: "ArticlesView",
        component: ArticlesView,
      },
      {
        path: ":categorySlug",
        name: "CategorieView",
        component: CategorieView,
      },
      {
        path: ":categorySlug/:id",
        name: "BlogPostView",
        component: BlogPostView,
      },
    ],
  },
  {
    path: "/customer/activate/:token/:isMedkus",
    name: "CustomerActivate",
    component: CustomerActivateLayout,
  },
];

// Let scrollBehavior own scrolling: without this, the browser restores the
// pre-reload position on refresh (landing users at the footer of long pages).
if ("scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      // vue-router persists the scroll position in history.state across page
      // reloads; only honor it for back/forward, never for a refresh.
      const isInitialNavigation = from.matched.length === 0;
      const navEntry = performance.getEntriesByType?.("navigation")[0] as
        | PerformanceNavigationTiming
        | undefined;
      if (isInitialNavigation && navEntry?.type === "reload") {
        return { top: 0 };
      }
      return savedPosition;
    }

    return { top: 0 };
  },
});

router.beforeEach((to, from, next) => {
  if (useUIStore().globalErrorIsOpen === true) {
    next(false);
  } else {
    next();
  }
});

// Only handles noindex for private pages — public pages set their own SEO via useSeo() + useI18n()
router.afterEach((to) => {
  const name = to.name as string | undefined;
  if (name && noindexRoutes.has(name)) {
    setSeo({ noindex: true });
  }
});

export default router;
